import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useLanguage } from "../App";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Calendar } from "./ui/calendar";
import {
  Clock, Check, ArrowLeft, ArrowRight, Sparkles,
  Calendar as CalendarIcon, Briefcase, CreditCard,
  Wand2, Plus, Minus, Zap, ThumbsUp, X, Trash2
} from "lucide-react";
import { format, isBefore, startOfToday } from "date-fns";
import { it, enUS } from "date-fns/locale";

/**
 * BookingModal — Smart Page V2 booking engine (multi-service)
 * Bottom-sheet on mobile, centered on desktop.
 */
const BookingModal = ({
  isOpen,
  onClose,
  salon,
  services,
  staff,
  slug,
  brandPrimary,
  brandSecondary,
  closurePeriods,
  weeklyClosedDays,
  initialService = null,
  onTrack = () => {},
}) => {
  const { language } = useLanguage();

  // Multi-service: array of service objects
  const [step, setStep] = useState(1);
  const [selectedServices, setSelectedServices] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [clientInfo, setClientInfo] = useState({
    first_name: "", last_name: "", email: "", phone: "", notes: ""
  });
  const [booking, setBooking] = useState(false);
  const [bookingComplete, setBookingComplete] = useState(false);

  // Payment
  const paymentSettings = salon?.payment_settings || {};
  const paymentEnabled = paymentSettings.payments_enabled || false;
  const paymentRequired = paymentSettings.require_payment || false;
  const allowDeposit = paymentSettings.allow_deposit !== false;
  const depositPercentage = paymentSettings.deposit_percentage || 30;
  const [paymentType, setPaymentType] = useState("full");
  const [processingPayment, setProcessingPayment] = useState(false);

  // Multi-service availability
  const allowMulti = (salon?.smartpage_settings?.allow_multi_service !== false);

  // AI features
  const [upsellSuggestions, setUpsellSuggestions] = useState([]);
  const [smartFillFound, setSmartFillFound] = useState(false);
  const [smartFillLoading, setSmartFillLoading] = useState(false);
  const [recommendedSlots, setRecommendedSlots] = useState([]);

  // Reset on open
  useEffect(() => {
    if (isOpen) {
      const initArr = initialService ? [initialService] : [];
      setSelectedServices(initArr);
      setStep(1);
      setSelectedDate(null);
      setSelectedTime(null);
      setSelectedStaff(null);
      setBookingComplete(false);
      onTrack("modal_open", { service_id: initialService?.service_id });
    }
  }, [isOpen, initialService]);

  // Lock body scroll
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = ""; };
    }
  }, [isOpen]);

  const totalDuration = selectedServices.reduce((sum, s) => sum + (s.duration || 0), 0);
  const totalPrice = selectedServices.reduce((sum, s) => sum + (parseFloat(s.price) || 0), 0);
  const aggregatedName = selectedServices.map((s) => s.name).join(" + ");

  const isSelected = (sid) => selectedServices.some((s) => s.service_id === sid);
  const toggleService = (svc) => {
    setSelectedServices((prev) => {
      const exists = prev.some((s) => s.service_id === svc.service_id);
      if (exists) return prev.filter((s) => s.service_id !== svc.service_id);
      // If multi-service disabled, replace
      if (!allowMulti) return [svc];
      return [...prev, svc];
    });
  };
  const removeService = (sid) => setSelectedServices((prev) => prev.filter((s) => s.service_id !== sid));

  const isDateClosed = (date) => {
    if (!date) return false;
    const dayNames = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
    if (weeklyClosedDays.includes(dayNames[date.getDay()])) return true;
    const dateStr = format(date, "yyyy-MM-dd");
    return closurePeriods.some(p => dateStr >= p.start_date && dateStr <= p.end_date);
  };

  // Fetch slots for ALL selected services (multi)
  useEffect(() => {
    const fetchSlots = async () => {
      if (selectedServices.length === 0 || !selectedDate) return;
      setLoadingSlots(true);
      try {
        const dateStr = format(selectedDate, "yyyy-MM-dd");
        const ids = selectedServices.map((s) => s.service_id).join(",");
        const response = await axios.get(`${API}/public/salon/${slug}/slots`, {
          params: { date: dateStr, service_ids: ids, staff_id: selectedStaff?.staff_id }
        });
        setAvailableSlots(response.data.slots || []);
      } catch (e) {
        setAvailableSlots([]);
      } finally {
        setLoadingSlots(false);
      }
    };
    fetchSlots();
  }, [selectedServices, selectedDate, selectedStaff, slug]);

  // AI upsell — only show services NOT already selected
  useEffect(() => {
    const fetchUpsell = async () => {
      if (selectedServices.length === 0 || !slug) {
        setUpsellSuggestions([]);
        return;
      }
      try {
        const response = await axios.post(`${API}/ai/upsell/public?slug=${slug}`, {
          service_id: selectedServices[0].service_id,
          client_phone: clientInfo.phone || null
        });
        // Filter out already-selected
        const existingIds = new Set(selectedServices.map((s) => s.service_id));
        const filtered = (response.data.suggestions || []).filter((s) => !existingIds.has(s.service_id));
        setUpsellSuggestions(filtered);
      } catch (e) {
        setUpsellSuggestions([]);
      }
    };
    const timer = setTimeout(fetchUpsell, 400);
    return () => clearTimeout(timer);
  }, [selectedServices, slug]);

  // AI smart slots
  useEffect(() => {
    const fetchSmartSlots = async () => {
      if (selectedServices.length === 0 || !selectedDate || !slug) return;
      try {
        const dateStr = format(selectedDate, "yyyy-MM-dd");
        const response = await axios.post(
          `${API}/ai/smart-slots?slug=${slug}&date=${dateStr}`,
          { service_id: selectedServices[0].service_id, client_phone: clientInfo.phone || null }
        );
        setRecommendedSlots(response.data.recommended_slots || []);
      } catch (e) { setRecommendedSlots([]); }
    };
    fetchSmartSlots();
  }, [selectedServices, selectedDate, slug]);

  const handleSmartFill = async (field, value) => {
    if (!value || !slug) return;
    const isPhone = field === "phone" && value.replace(/\D/g, "").length >= 6;
    const isEmail = field === "email" && value.includes("@") && value.includes(".");
    if (!isPhone && !isEmail) return;
    setSmartFillLoading(true);
    try {
      const response = await axios.post(`${API}/ai/smart-fill?slug=${slug}`, {
        phone: field === "phone" ? value : null,
        email: field === "email" ? value : null
      });
      if (response.data.found && response.data.client_data) {
        const data = response.data.client_data;
        setClientInfo(prev => ({
          ...prev,
          first_name: data.first_name || prev.first_name,
          last_name: data.last_name || prev.last_name,
          email: data.email || prev.email,
          phone: data.phone || prev.phone
        }));
        setSmartFillFound(true);
        toast.success(language === "it" ? "Bentornato! 👋" : "Welcome back! 👋");
      }
    } catch (e) {/* silent */}
    finally { setSmartFillLoading(false); }
  };

  const calculateDepositAmount = () => Math.max(totalPrice * (depositPercentage / 100), 5.0).toFixed(2);

  const handleBooking = async () => {
    if (selectedServices.length === 0 || !selectedDate || !selectedTime || !clientInfo.first_name || !clientInfo.last_name || !clientInfo.phone) {
      toast.error(language === "it" ? "Compila tutti i campi obbligatori" : "Fill all required fields");
      return;
    }
    const shouldPay = paymentEnabled && (paymentRequired || paymentType !== "skip");
    if (shouldPay && totalPrice > 0) {
      return handlePaymentCheckout();
    }
    setBooking(true);
    try {
      await axios.post(`${API}/public/salon/${slug}/book`, {
        service_ids: selectedServices.map((s) => s.service_id),
        staff_id: selectedStaff?.staff_id || selectedTime.staff[0]?.staff_id,
        client_first_name: clientInfo.first_name,
        client_last_name: clientInfo.last_name,
        client_email: clientInfo.email || null,
        client_phone: clientInfo.phone,
        date: format(selectedDate, "yyyy-MM-dd"),
        time: selectedTime.time,
        notes: clientInfo.notes
      });
      setBookingComplete(true);
      onTrack("booking_completed", { service_count: selectedServices.length, price: totalPrice });
      toast.success(language === "it" ? "Prenotazione confermata!" : "Booking confirmed!");
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === "it" ? "Errore" : "Error"));
    } finally {
      setBooking(false);
    }
  };

  const handlePaymentCheckout = async () => {
    setProcessingPayment(true);
    try {
      const response = await axios.post(`${API}/payments/booking/checkout`, {
        salon_slug: slug,
        service_ids: selectedServices.map((s) => s.service_id),
        appointment_details: {
          client_name: `${clientInfo.first_name} ${clientInfo.last_name}`,
          client_first_name: clientInfo.first_name,
          client_last_name: clientInfo.last_name,
          client_email: clientInfo.email || null,
          client_phone: clientInfo.phone,
          date: format(selectedDate, "yyyy-MM-dd"),
          time: selectedTime.time,
          staff_id: selectedStaff?.staff_id || selectedTime.staff[0]?.staff_id,
          notes: clientInfo.notes,
          service_ids: selectedServices.map((s) => s.service_id),
        },
        payment_type: paymentType,
        origin_url: window.location.origin
      });
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === "it" ? "Errore pagamento" : "Payment error"));
      setProcessingPayment(false);
    }
  };

  if (!isOpen) return null;

  const formatDuration = (mins) => {
    if (mins < 60) return `${mins} min`;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return m > 0 ? `${h}h ${m}min` : `${h}h`;
  };

  const showPrices = salon?.smartpage_settings?.show_prices !== false;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm animate-in fade-in duration-200"
      onClick={onClose}
      data-testid="booking-modal-overlay"
    >
      <div
        className="w-full sm:max-w-2xl bg-white dark:bg-slate-900 rounded-t-3xl sm:rounded-3xl shadow-2xl max-h-[95vh] sm:max-h-[90vh] flex flex-col animate-in slide-in-from-bottom sm:zoom-in-95 duration-300"
        onClick={(e) => e.stopPropagation()}
        data-testid="booking-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-700 sticky top-0 bg-white dark:bg-slate-900 rounded-t-3xl z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${brandPrimary}, ${brandSecondary})` }}>
              <CalendarIcon className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="font-bold text-slate-900 dark:text-white text-base">{language === "it" ? "Prenota appuntamento" : "Book appointment"}</h2>
              <p className="text-xs text-slate-500">{salon?.name}</p>
            </div>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 flex items-center justify-center transition-colors" data-testid="close-booking-modal" aria-label="Close">
            <X className="w-5 h-5 text-slate-600 dark:text-slate-300" />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-5 py-5">
          {bookingComplete ? (
            <div className="text-center py-8">
              <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 animate-in zoom-in duration-500" style={{ background: `linear-gradient(135deg, ${brandPrimary}, ${brandSecondary})` }}>
                <Check className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">{language === "it" ? "Prenotazione Confermata!" : "Booking Confirmed!"}</h3>
              <p className="text-slate-500 dark:text-slate-400 mb-6">{language === "it" ? "Riceverai una conferma via email" : "You'll receive an email confirmation"}</p>
              <div className="bg-slate-50 dark:bg-slate-800 rounded-2xl p-5 text-left space-y-3 mb-6">
                <div className="flex justify-between"><span className="text-slate-500">{language === "it" ? "Servizi" : "Services"}</span><span className="font-semibold text-right max-w-[60%] truncate">{aggregatedName}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">{language === "it" ? "Durata totale" : "Total duration"}</span><span className="font-semibold">{formatDuration(totalDuration)}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">{language === "it" ? "Data" : "Date"}</span><span className="font-semibold">{format(selectedDate, "dd/MM/yyyy")}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">{language === "it" ? "Ora" : "Time"}</span><span className="font-semibold">{selectedTime?.time}</span></div>
                {showPrices && totalPrice > 0 && (
                  <div className="flex justify-between border-t pt-2 mt-2"><span className="font-semibold">Totale</span><span className="font-bold text-lg" style={{ color: brandPrimary }}>€{totalPrice.toFixed(2)}</span></div>
                )}
              </div>
              <Button onClick={onClose} className="w-full rounded-full py-6 font-semibold" style={{ background: `linear-gradient(135deg, ${brandPrimary}, ${brandSecondary})` }} data-testid="booking-complete-close">
                {language === "it" ? "Chiudi" : "Close"}
              </Button>
            </div>
          ) : (
            <>
              {/* Progress */}
              <div className="flex items-center justify-center gap-3 mb-6">
                {[
                  { n: 1, l: language === "it" ? "Servizi" : "Services", i: Briefcase },
                  { n: 2, l: language === "it" ? "Data/Ora" : "Date/Time", i: CalendarIcon },
                  { n: 3, l: language === "it" ? "Conferma" : "Confirm", i: Check },
                ].map((s, i) => (
                  <React.Fragment key={s.n}>
                    <div className="flex flex-col items-center">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${step >= s.n ? "text-white shadow-md" : "bg-slate-200 dark:bg-slate-700 text-slate-400"}`} style={step >= s.n ? { background: `linear-gradient(135deg, ${brandPrimary}, ${brandSecondary})` } : {}}>
                        {step > s.n ? <Check className="w-4 h-4" /> : <s.i className="w-4 h-4" />}
                      </div>
                      <span className={`text-[10px] mt-1 ${step >= s.n ? "text-slate-900 dark:text-white font-medium" : "text-slate-400"}`}>{s.l}</span>
                    </div>
                    {i < 2 && <div className={`w-8 sm:w-12 h-0.5 ${step > s.n ? "" : "bg-slate-200 dark:bg-slate-700"}`} style={step > s.n ? { background: `linear-gradient(90deg, ${brandPrimary}, ${brandSecondary})` } : {}} />}
                  </React.Fragment>
                ))}
              </div>

              {/* Step 1: Services (multi-select) */}
              {step === 1 && (
                <div className="animate-in fade-in duration-300 pb-20">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-xl font-bold text-slate-900 dark:text-white">
                      {language === "it" ? "Scegli i servizi" : "Pick services"}
                    </h3>
                    {allowMulti && (
                      <span className="text-xs text-slate-500">{language === "it" ? "Seleziona uno o più" : "Select one or more"}</span>
                    )}
                  </div>
                  <div className="space-y-3">
                    {services.map((service) => {
                      const sel = isSelected(service.service_id);
                      return (
                        <button
                          key={service.service_id}
                          onClick={() => {
                            toggleService(service);
                            if (sel) onTrack("service_removed", { service_id: service.service_id });
                            else onTrack("service_selected", { service_id: service.service_id });
                          }}
                          className={`w-full p-4 rounded-2xl border-2 text-left transition-all group ${sel ? "shadow-md" : "bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:border-violet-400"}`}
                          style={sel ? { borderColor: brandPrimary, background: `linear-gradient(135deg, ${brandPrimary}10, ${brandSecondary}10)` } : {}}
                          data-testid={`modal-service-${service.service_id}`}
                          data-selected={sel ? "true" : "false"}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-7 h-7 rounded-full flex-shrink-0 flex items-center justify-center transition-all ${sel ? "shadow-md text-white" : "border-2 border-slate-300 dark:border-slate-600"}`} style={sel ? { background: `linear-gradient(135deg, ${brandPrimary}, ${brandSecondary})` } : {}}>
                              {sel ? <Check className="w-4 h-4" /> : <Plus className="w-4 h-4 text-slate-400" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="font-semibold text-slate-900 dark:text-white">{service.name}</div>
                              {service.description && <div className="text-xs text-slate-500 mt-0.5 line-clamp-1">{service.description}</div>}
                              <div className="flex items-center gap-1.5 mt-1.5 text-xs text-slate-500"><Clock className="w-3.5 h-3.5" />{service.duration} min</div>
                            </div>
                            {showPrices && (
                              <div className="text-xl font-bold flex-shrink-0" style={{ color: brandPrimary }}>€{service.price}</div>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Step 2: Date/Time */}
              {step === 2 && (
                <div className="animate-in fade-in duration-300 pb-20">
                  <button onClick={() => setStep(1)} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 mb-4" data-testid="modal-back-step1">
                    <ArrowLeft className="w-4 h-4" />{language === "it" ? "Indietro" : "Back"}
                  </button>

                  {/* Selected services list */}
                  <div className="rounded-2xl p-3 mb-4 space-y-2" style={{ background: `linear-gradient(135deg, ${brandPrimary}15, ${brandSecondary}15)` }}>
                    {selectedServices.map((s) => (
                      <div key={s.service_id} className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2 min-w-0">
                          <Check className="w-3.5 h-3.5 flex-shrink-0" style={{ color: brandPrimary }} />
                          <span className="font-medium truncate">{s.name}</span>
                          <span className="text-xs text-slate-500">· {s.duration} min</span>
                        </div>
                        {showPrices && <span className="font-semibold text-sm flex-shrink-0" style={{ color: brandPrimary }}>€{s.price}</span>}
                      </div>
                    ))}
                    {selectedServices.length > 1 && (
                      <div className="border-t border-slate-300/30 pt-2 flex justify-between text-sm font-semibold">
                        <span>Totale · {formatDuration(totalDuration)}</span>
                        {showPrices && <span style={{ color: brandPrimary }}>€{totalPrice.toFixed(2)}</span>}
                      </div>
                    )}
                  </div>

                  {/* Upsell */}
                  {allowMulti && upsellSuggestions.length > 0 && (
                    <div className="mb-4 p-3 rounded-xl bg-gradient-to-r from-violet-50 to-purple-50 dark:from-violet-900/20 dark:to-purple-900/20 border border-violet-200 dark:border-violet-700">
                      <div className="flex items-center gap-2 mb-2">
                        <Sparkles className="w-4 h-4 text-violet-500" />
                        <span className="text-xs font-medium text-violet-700 dark:text-violet-300">{language === "it" ? "Aggiungi anche" : "Add also"}</span>
                      </div>
                      <div className="space-y-1.5">
                        {upsellSuggestions.slice(0, 2).map((s, i) => (
                          <button
                            key={i}
                            onClick={() => {
                              const full = services.find((sv) => sv.service_id === s.service_id);
                              if (full) toggleService(full);
                            }}
                            className="w-full flex justify-between items-center text-xs bg-white dark:bg-slate-800 rounded-lg px-3 py-2 hover:bg-violet-50 dark:hover:bg-violet-950/30 transition-colors"
                            data-testid={`upsell-add-${i}`}
                          >
                            <span className="flex items-center gap-2 min-w-0"><Plus className="w-3 h-3 text-violet-500" /><span className="truncate">{s.name}</span></span>
                            {showPrices && <span className="font-semibold flex-shrink-0" style={{ color: brandPrimary }}>+€{s.price}</span>}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {staff.length > 0 && (
                    <div className="mb-4">
                      <p className="text-xs font-medium text-slate-700 dark:text-slate-300 mb-2">{language === "it" ? "Operatore (opz.)" : "Staff (optional)"}</p>
                      <div className="flex flex-wrap gap-2">
                        <button onClick={() => setSelectedStaff(null)} className={`px-3 py-1.5 rounded-full text-xs font-medium ${!selectedStaff ? "text-white shadow" : "bg-slate-100 dark:bg-slate-700"}`} style={!selectedStaff ? { background: `linear-gradient(135deg, ${brandPrimary}, ${brandSecondary})` } : {}}>{language === "it" ? "Qualsiasi" : "Any"}</button>
                        {staff.map(s => (
                          <button key={s.staff_id} onClick={() => setSelectedStaff(s)} className={`px-3 py-1.5 rounded-full text-xs font-medium ${selectedStaff?.staff_id === s.staff_id ? "text-white shadow" : "bg-slate-100 dark:bg-slate-700"}`} style={selectedStaff?.staff_id === s.staff_id ? { background: `linear-gradient(135deg, ${brandPrimary}, ${brandSecondary})` } : {}}>{s.name}</button>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="bg-white dark:bg-slate-800 rounded-2xl p-3 border border-slate-200 dark:border-slate-700 mb-4">
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={(d) => !isDateClosed(d) && setSelectedDate(d)}
                      disabled={(d) => isBefore(d, startOfToday()) || isDateClosed(d)}
                      locale={language === "it" ? it : enUS}
                      className="mx-auto"
                    />
                  </div>

                  {selectedDate && (
                    <div>
                      <p className="text-xs font-medium text-slate-700 dark:text-slate-300 mb-2">
                        {language === "it" ? "Orari" : "Times"} — {format(selectedDate, "dd/MM")}
                        <span className="text-slate-400 ml-1">· {formatDuration(totalDuration)}</span>
                      </p>
                      {recommendedSlots.length > 0 && availableSlots.length > 0 && (
                        <div className="mb-3 p-2 rounded-xl bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 border border-emerald-200 dark:border-emerald-700">
                          <div className="flex items-center gap-1 mb-2"><Zap className="w-3.5 h-3.5 text-emerald-600" /><span className="text-xs font-medium text-emerald-700 dark:text-emerald-300">{language === "it" ? "Consigliati" : "Recommended"}</span></div>
                          <div className="flex flex-wrap gap-1.5">
                            {recommendedSlots.map((slot, i) => (
                              <button key={i} onClick={() => { setSelectedTime(slot); setStep(3); }} className="px-3 py-1.5 rounded-lg bg-emerald-500 text-white text-xs font-medium" data-testid={`modal-rec-slot-${i}`}>
                                <Sparkles className="w-3 h-3 inline mr-1" />{slot.time}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      {loadingSlots ? (
                        <div className="flex justify-center py-6"><div className="w-6 h-6 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" /></div>
                      ) : availableSlots.length > 0 ? (
                        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                          {availableSlots.map((slot, i) => (
                            <button
                              key={i}
                              onClick={() => { setSelectedTime(slot); setStep(3); }}
                              className="p-2.5 rounded-xl text-sm font-medium bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:border-violet-400 transition-all"
                              data-testid={`modal-slot-${i}`}
                            >
                              {slot.time}
                            </button>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-6 bg-slate-50 dark:bg-slate-800/50 rounded-xl text-sm text-slate-500">
                          {language === "it" ? "Nessun orario disponibile" : "No times available"}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Step 3: Client info */}
              {step === 3 && (
                <div className="animate-in fade-in duration-300">
                  <button onClick={() => setStep(2)} className="flex items-center gap-1 text-sm text-slate-500 mb-4" data-testid="modal-back-step2"><ArrowLeft className="w-4 h-4" />{language === "it" ? "Indietro" : "Back"}</button>

                  <div className="rounded-2xl p-4 mb-4" style={{ background: `linear-gradient(135deg, ${brandPrimary}15, ${brandSecondary}15)` }}>
                    <h4 className="font-semibold text-sm mb-2">{language === "it" ? "Riepilogo" : "Summary"}</h4>
                    <div className="space-y-1.5 text-xs">
                      {selectedServices.map((s) => (
                        <div key={s.service_id} className="flex justify-between">
                          <span className="text-slate-600 dark:text-slate-300 truncate pr-2">{s.name} <span className="text-slate-400">· {s.duration}m</span></span>
                          {showPrices && <span className="font-medium flex-shrink-0">€{s.price}</span>}
                        </div>
                      ))}
                      <div className="flex justify-between border-t border-slate-300/30 pt-1.5 mt-1.5">
                        <span className="text-slate-500">{format(selectedDate, "dd/MM/yyyy")} · {selectedTime?.time}</span>
                        {showPrices && <span className="font-bold" style={{ color: brandPrimary }}>€{totalPrice.toFixed(2)}</span>}
                      </div>
                    </div>
                  </div>

                  {smartFillFound && (
                    <div className="mb-3 p-2.5 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 flex items-center gap-2">
                      <ThumbsUp className="w-4 h-4 text-emerald-600" />
                      <span className="text-xs text-emerald-700 dark:text-emerald-300">{language === "it" ? "Bentornato! Dati compilati." : "Welcome back! Auto-filled."}</span>
                    </div>
                  )}

                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1.5 flex items-center gap-1.5">
                        {language === "it" ? "Telefono *" : "Phone *"}
                        {smartFillLoading && <Wand2 className="w-3 h-3 animate-pulse text-violet-500" />}
                      </label>
                      <Input type="tel" value={clientInfo.phone} onChange={e => setClientInfo({ ...clientInfo, phone: e.target.value })} onBlur={e => handleSmartFill("phone", e.target.value)} placeholder="+39 333 1234567" className="rounded-xl" data-testid="modal-phone" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1.5">{language === "it" ? "Nome *" : "First Name *"}</label>
                        <Input value={clientInfo.first_name} onChange={e => setClientInfo({ ...clientInfo, first_name: e.target.value })} className="rounded-xl" data-testid="modal-first-name" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1.5">{language === "it" ? "Cognome *" : "Last Name *"}</label>
                        <Input value={clientInfo.last_name} onChange={e => setClientInfo({ ...clientInfo, last_name: e.target.value })} className="rounded-xl" data-testid="modal-last-name" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1.5">Email ({language === "it" ? "opz." : "opt."})</label>
                      <Input type="email" value={clientInfo.email} onChange={e => setClientInfo({ ...clientInfo, email: e.target.value })} onBlur={e => !smartFillFound && handleSmartFill("email", e.target.value)} className="rounded-xl" data-testid="modal-email" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1.5">{language === "it" ? "Note (opz.)" : "Notes (opt.)"}</label>
                      <Input value={clientInfo.notes} onChange={e => setClientInfo({ ...clientInfo, notes: e.target.value })} className="rounded-xl" data-testid="modal-notes" />
                    </div>
                  </div>

                  {paymentEnabled && totalPrice > 0 && (
                    <div className="mt-4 p-3 rounded-2xl border border-slate-200 dark:border-slate-700">
                      <div className="flex items-center gap-2 mb-2.5"><CreditCard className="w-4 h-4 text-violet-500" /><span className="font-semibold text-sm">{language === "it" ? "Pagamento" : "Payment"}</span></div>
                      <div className="space-y-2">
                        <label className={`flex items-center gap-2 p-2.5 rounded-xl border-2 cursor-pointer text-sm ${paymentType === "full" ? "border-violet-500 bg-violet-50 dark:bg-violet-900/20" : "border-slate-200"}`}>
                          <input type="radio" checked={paymentType === "full"} onChange={() => setPaymentType("full")} />
                          <span className="flex-1">{language === "it" ? "Paga ora" : "Pay now"}</span>
                          <span className="font-bold" style={{ color: brandPrimary }}>€{totalPrice.toFixed(2)}</span>
                        </label>
                        {allowDeposit && (
                          <label className={`flex items-center gap-2 p-2.5 rounded-xl border-2 cursor-pointer text-sm ${paymentType === "deposit" ? "border-violet-500 bg-violet-50 dark:bg-violet-900/20" : "border-slate-200"}`}>
                            <input type="radio" checked={paymentType === "deposit"} onChange={() => setPaymentType("deposit")} />
                            <span className="flex-1">{language === "it" ? `Acconto ${depositPercentage}%` : `Deposit ${depositPercentage}%`}</span>
                            <span className="font-bold" style={{ color: brandPrimary }}>€{calculateDepositAmount()}</span>
                          </label>
                        )}
                        {!paymentRequired && (
                          <label className={`flex items-center gap-2 p-2.5 rounded-xl border-2 cursor-pointer text-sm ${paymentType === "skip" ? "border-slate-500 bg-slate-50 dark:bg-slate-700" : "border-slate-200"}`}>
                            <input type="radio" checked={paymentType === "skip"} onChange={() => setPaymentType("skip")} />
                            <span className="flex-1">{language === "it" ? "Paga in sede" : "Pay at venue"}</span>
                          </label>
                        )}
                      </div>
                    </div>
                  )}

                  <Button
                    onClick={handleBooking}
                    disabled={!clientInfo.first_name || !clientInfo.last_name || !clientInfo.phone || booking || processingPayment}
                    className="w-full mt-5 rounded-full py-6 font-semibold shadow-xl hover:shadow-2xl disabled:opacity-50 transition-all"
                    style={{ background: `linear-gradient(135deg, ${brandPrimary}, ${brandSecondary})` }}
                    data-testid="modal-confirm-booking"
                  >
                    {(booking || processingPayment) ? (
                      <span className="flex items-center gap-2"><div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />{processingPayment ? (language === "it" ? "Pagamento..." : "Payment...") : (language === "it" ? "Prenoto..." : "Booking...")}</span>
                    ) : paymentEnabled && totalPrice > 0 && paymentType !== "skip" ? (
                      <span className="flex items-center gap-2"><CreditCard className="w-5 h-5" />{language === "it" ? `Paga e prenota` : `Pay & Book`}</span>
                    ) : (
                      <span className="flex items-center gap-2"><Sparkles className="w-5 h-5" />{language === "it" ? "Conferma" : "Confirm"}</span>
                    )}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Sticky bottom bar (Step 1 — Continue with selected services) */}
        {!bookingComplete && step === 1 && selectedServices.length > 0 && (
          <div className="border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-5 py-3 flex items-center justify-between gap-3 sticky bottom-0" data-testid="multi-bottom-bar">
            <div className="min-w-0 flex-1">
              <p className="text-xs text-slate-500">
                {selectedServices.length} {language === "it"
                  ? (selectedServices.length === 1 ? "servizio" : "servizi")
                  : (selectedServices.length === 1 ? "service" : "services")} · {formatDuration(totalDuration)}
              </p>
              {showPrices && <p className="font-bold text-lg" style={{ color: brandPrimary }}>€{totalPrice.toFixed(2)}</p>}
            </div>
            <Button
              onClick={() => setStep(2)}
              className="rounded-full px-5 py-5 font-semibold"
              style={{ background: `linear-gradient(135deg, ${brandPrimary}, ${brandSecondary})` }}
              data-testid="multi-continue-btn"
            >
              {language === "it" ? "Continua" : "Continue"}
              <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BookingModal;
